import { useState } from 'react';
import {
  facilityResearch, intelOperatorBacktest, intelOperatorDetail, intelOperatorReimbursement, operatorResearch,
  type ApiClient, type OperatorBacktestResult, type OperatorBuilding, type OperatorDetail,
} from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { HonestyBadge } from '../../ui/HonestyBadge';
import { LiveResearch } from '../../ui/LiveResearch';
import { OwnershipNetwork } from './OwnershipNetwork';
import { FleetScope } from '../../session/DemoScope';
import { useBuildText } from '../../ui/buildLabels';

// One operator drilled down, restored from the production console (operator_detail.dart
// and relationship_network_view.dart, the ownership network): the portfolio summary,
// EVERY building it owns (each one opens its own detail), the beds by state with their
// concentration, who else holds or runs them (the CMS owner rows), the arms that have
// no feed yet as named empty slots, the modeled reimbursement across them, the operator
// backtest and live research. Not in the V3 design: it opens under the operator's row
// on North Star > Operators. Every figure comes from GET /operators/:org; nothing here
// is invented, and an unknown is said to be unknown.

const n0 = (v: number | null | undefined) => (v == null ? '–' : Math.round(v).toLocaleString('en-US'));
const pct = (v: number | null | undefined) => (v == null ? '–' : (v * 100).toFixed(1) + '%');
const $ = (v: number) => '$' + (v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : Math.round(v / 1000) + 'K');
const num = (v: unknown) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const CAP = 'font-size:14px;letter-spacing:.09em;text-transform:uppercase;color:#54606C;font-weight:600;';

/**
 * The arms of the relationship map that have no feed yet (production's waitingOn). They
 * are named, never hidden and never filled with made-up partners.
 */
export const EMPTY_ARMS: [string, string][] = [
  ['Suppliers', 'EHR, pharmacy, lab, agency staffing, therapy'],
  ['Demand', 'referring hospitals, physician groups, payer mix'],
  ['Referrals', 'discharge planners, placement agencies, ACOs'],
  ['Peers', 'buildings competing for the same census'],
];

export function backtestLine(b: OperatorBacktestResult): string {
  if (!b.available) return b.reason ?? 'Not enough comparable cost reports to backtest.';
  return `${b.n ?? 0} buildings with comparable cost reports: revenue CAGR ${pct(b.avgRevenueCagr)}, margin ${b.avgMarginDelta != null && b.avgMarginDelta >= 0 ? '+' : ''}${pct(b.avgMarginDelta)}, ${pct(b.pctMarginImproved)} improved margin.`;
}

/** Beds by state, largest first, and the HHI of that split (0 to 10,000), as production drew it. */
export function bedsByState(facilities: OperatorBuilding[]): { rows: { state: string; buildings: number; beds: number }[]; hhi: number | null } {
  const by = new Map<string, { buildings: number; beds: number }>();
  for (const f of facilities) {
    if (!f.state) continue;
    const b = by.get(f.state) ?? { buildings: 0, beds: 0 };
    b.buildings += 1;
    b.beds += num(f.beds) ?? 0;
    by.set(f.state, b);
  }
  const rows = [...by.entries()].map(([state, b]) => ({ state, ...b })).sort((a, b) => b.beds - a.beds || b.buildings - a.buildings || a.state.localeCompare(b.state));
  const total = rows.reduce((s, r) => s + r.beds, 0);
  const hhi = total > 0 ? Math.round(rows.reduce((s, r) => s + (r.beds / total) ** 2, 0) * 10000) : null;
  return { rows, hhi };
}

export type SendToDealDesk = (f: OperatorBuilding) => Promise<string | null>;

function Building({ f, chainBeds, onDealDesk }: { f: OperatorBuilding; chainBeds: number; onDealDesk?: SendToDealDesk }) {
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const beds = num(f.beds);
  const comp = num(f.composite_score);
  const occ = num(f.occupancy);
  const kv: [string, string][] = [
    ['CCN', f.ccn ?? 'none (not CMS-certified)'],
    ['Type', f.type ?? '–'],
    ['Beds', n0(beds)],
    ['Share of the chain', beds == null || chainBeds <= 0 ? '–' : ((beds / chainBeds) * 100).toFixed(1) + '% of beds'],
    ['CMS stars', f.overall_rating == null ? 'not rated' : String(f.overall_rating)],
    ['Composite', comp == null ? 'not scored' : Math.round(comp) + ' / 100'],
    ['Grade', f.score_grade ?? '–'],
    ['Occupancy', occ == null ? '–' : pct(occ > 1 ? occ / 100 : occ)],
    ['Severe deficiencies', f.severe_deficiency_count == null ? '–' : String(f.severe_deficiency_count)],
    ['CMS abuse flag', f.abuse_icon == null ? '–' : f.abuse_icon ? 'yes' : 'no'],
    ['PE-backed (name heuristic)', f.pe_backed == null ? '–' : f.pe_backed ? 'yes' : 'no'],
  ];
  return (
    <div data-testid="intel-operator-building" style={sx('display:flex;flex-direction:column;gap:6px;margin:4px 0 8px 12px;padding:8px 10px;border-left:2px solid rgba(47,92,151,.34);')}>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fill,minmax(210px,1fr));gap:4px 16px;')}>
        {kv.map(([k, v]) => (
          <span key={k} style={sx('display:flex;justify-content:space-between;gap:8px;font-size:14px;color:#3C454F;')}>
            <span>{k}</span>
            <span style={sx('font-weight:700;color:#22272E;font-variant-numeric:tabular-nums;')}>{v}</span>
          </span>
        ))}
      </div>
      {onDealDesk && (
        <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;')}>
          <a
            href="#"
            aria-disabled={busy || undefined}
            onClick={(e) => {
              e.preventDefault();
              if (busy) return;
              setBusy(true);
              setMsg(null);
              onDealDesk(f)
                .then(setMsg, (err: unknown) => setMsg(err instanceof Error ? err.message : 'Could not send it to the Deal Desk.'))
                .finally(() => setBusy(false));
            }}
            style={sx('display:inline-flex;align-items:center;height:28px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;background:#2F5C97;color:#fff;')}
          >
            {busy ? 'Sending…' : 'Send to Deal Desk'}
          </a>
          {msg && <span role="status" style={sx('font-size:14px;color:#54606C;')}>{msg}</span>}
        </div>
      )}
      <LiveResearch
        testId="intel-building-research"
        title={'What is reported about ' + f.name}
        runLabel="Research this building"
        load={(c, refresh) => facilityResearch(c, { params: { id: f.id }, query: refresh ? { refresh: '1' } : {} })}
      />
    </div>
  );
}

export function OperatorDrawer({ client, org, onDealDesk }: { client: ApiClient; org: string; onDealDesk?: SendToDealDesk }) {
  const bt = useBuildText();
  const d = useQuery<OperatorDetail>((signal) => intelOperatorDetail(client, { params: { org }, signal }), [client, org]);
  const r = useQuery((signal) => intelOperatorReimbursement(client, { params: { org }, signal }), [client, org]);
  const b = useQuery((signal) => intelOperatorBacktest(client, { params: { org }, signal }), [client, org]);
  const [openId, setOpenId] = useState<string | null>(null);
  const s = d.data?.summary;
  const facilities = d.data?.facilities ?? [];
  const chainBeds = facilities.reduce((t, f) => t + (num(f.beds) ?? 0), 0);
  const geo = bedsByState(facilities);
  const owners = d.data?.owners ?? [];
  return (
    <div data-testid="intel-operator" style={sx('display:flex;flex-direction:column;gap:7px;padding:8px 0 12px;border-bottom:1px solid rgba(34,39,46,.1);')}>
      {d.loading && !d.data && <span style={sx('font-size:14px;color:#54606C;')}>Loading the operator…</span>}
      {d.error && <span role="alert" style={sx('font-size:14px;color:#B3261E;')}>{d.error.message}</span>}
      {s && (
        <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>
          {`${s.facilityCount} buildings in ${s.states.join(', ')} · ${n0(s.totalBeds)} beds · average ${s.avgRating ?? '–'} stars · ${s.distressCount} showing distress${s.peBacked ? ' · PE-backed (name heuristic)' : ''}`}
        </span>
      )}
      {r.data && (
        <span style={sx('font-size:16px;color:#3C454F;')}>
          {bt(`Modeled reimbursement across them: ${$(r.data.reimbursementAnnual)} a year, ${$(r.data.opportunityAnnual)} with state pools · MODELED`)}
        </span>
      )}
      {b.data && <span style={sx('font-size:16px;color:#3C454F;')}>{'Backtest: ' + backtestLine(b.data)}</span>}

      {/* A network across operators: every figure keeps its own tag. */}
      {d.data && (
        <FleetScope>
          <OwnershipNetwork org={org} d={d.data} onDealDesk={onDealDesk} />
        </FleetScope>
      )}

      {d.data && (
        <div data-testid="intel-operator-buildings" style={sx('display:flex;flex-direction:column;gap:2px;margin-top:4px;')}>
          <div style={sx('display:flex;align-items:center;gap:8px;')}>
            <span style={sx(CAP)}>{`Buildings owned · ${facilities.length}`}</span>
            <HonestyBadge flags={[d.data]} figure="operator buildings" />
            <span style={sx('font-size:14px;color:#54606C;')}>CMS owner of record · click one to open it</span>
          </div>
          <div style={sx('display:flex;flex-direction:column;max-height:320px;overflow-y:auto;')}>
            {facilities.map((f) => {
              const beds = num(f.beds);
              const share = beds == null || chainBeds <= 0 ? null : (beds / chainBeds) * 100;
              const line = [
                [f.city, f.state].filter(Boolean).join(', ') || null,
                beds != null ? beds + ' beds' : null,
                f.overall_rating != null ? f.overall_rating + ' stars' : null,
                f.score_grade ? 'grade ' + f.score_grade : null,
                share != null ? share.toFixed(share >= 10 ? 0 : 1) + '% of beds' : null,
              ].filter(Boolean).join(' · ');
              const open = openId === f.id;
              return (
                <div key={f.id}>
                  <a
                    href="#"
                    aria-expanded={open}
                    onClick={(e) => { e.preventDefault(); setOpenId(open ? null : f.id); }}
                    style={sx('display:flex;gap:8px;align-items:baseline;padding:3px 0;font-size:14px;color:#22272E;text-decoration:none;')}
                  >
                    <span style={sx('font-weight:600;')}>{f.name}</span>
                    <span style={sx('color:#54606C;')}>{line}</span>
                  </a>
                  {open && <Building f={f} chainBeds={chainBeds} onDealDesk={onDealDesk} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {d.data && geo.rows.length > 0 && (
        <span data-testid="intel-operator-geo" style={sx('font-size:14px;color:#3C454F;')}>
          {'By state: ' + geo.rows.map((g) => `${g.state} ${g.buildings} (${n0(g.beds)} beds)`).join(', ') +
            (geo.hhi == null ? '' : ` · concentration (HHI, beds by state) ${n0(geo.hhi)}`)}
        </span>
      )}

      {d.data && (
        <div data-testid="intel-operator-owners" style={sx('display:flex;flex-direction:column;gap:2px;margin-top:4px;')}>
          <div style={sx('display:flex;align-items:center;gap:8px;')}>
            <span style={sx(CAP)}>Also holding or running them</span>
            {d.data.ownersAvailable !== false && <HonestyBadge flags={[d.data]} figure="operator owners" />}
          </div>
          {d.data.ownersAvailable === false ? (
            <span style={sx('font-size:14px;color:#54606C;')}>{'Not known: ' + (d.data.ownersReason ?? 'the CMS owner rows are not available.')}</span>
          ) : owners.length === 0 ? (
            <span style={sx('font-size:14px;color:#54606C;')}>CMS lists no other organization with an ownership or control role on these buildings.</span>
          ) : (
            owners.map((o) => (
              <span key={o.name} style={sx('font-size:14px;color:#22272E;')}>
                {[
                  o.name,
                  `${o.buildings} of ${facilities.length} buildings`,
                  o.roles.length ? o.roles.map((x) => x.toLowerCase()).join(', ') : null,
                  o.maxPct != null ? `up to ${o.maxPct}%` : null,
                  o.peLike ? 'PE / REIT by name (heuristic)' : null,
                ].filter(Boolean).join(' · ')}
              </span>
            ))
          )}
        </div>
      )}

      {d.data && (
        <span data-testid="intel-operator-arms" style={sx('font-size:14px;color:#54606C;line-height:1.5;')}>
          {'No feed yet, so not known (not zero): ' + EMPTY_ARMS.map(([arm, wants]) => `${arm} (${wants})`).join('; ') + '.'}
        </span>
      )}

      <LiveResearch
        testId="intel-operator-research"
        title={'What is reported about ' + org}
        runLabel="Research this operator"
        load={(c, refresh) => operatorResearch(c, { params: { org }, query: refresh ? { refresh: '1' } : {} })}
      />
    </div>
  );
}
